import { useEffect, useId, useMemo, useRef, useState } from 'react'

import { filterSuggestions } from '../screens/catalogFilters'

type FilterComboboxProps = {
  label: string
  value: string
  options: string[]
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  maxOptions?: number
  listAllOnFocus?: boolean
}

export const FilterCombobox = ({
  label,
  value,
  options,
  onChange,
  placeholder,
  className,
  disabled = false,
  maxOptions = 12,
  listAllOnFocus = false,
}: FilterComboboxProps) => {
  const listId = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)

  const filteredOptions = useMemo(() => {
    const query = value.trim()
    if (!query) {
      return listAllOnFocus ? options.slice(0, maxOptions) : []
    }
    return filterSuggestions(options, query, maxOptions)
  }, [listAllOnFocus, maxOptions, options, value])

  // Сбрасываем activeIndex при изменении списка
  useEffect(() => {
    if (activeIndex >= filteredOptions.length) {
      setActiveIndex(filteredOptions.length - 1)
    }
  }, [filteredOptions, activeIndex])

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [])

  // Клавиатурная навигация — ARIA combobox pattern
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return

    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault()
        setOpen(true)
        if (filteredOptions.length === 0) return
        setActiveIndex((prev) => (prev + 1) % filteredOptions.length)
        break
      }

      case 'ArrowUp': {
        event.preventDefault()
        setOpen(true)
        if (filteredOptions.length === 0) return
        setActiveIndex((prev) =>
          prev <= 0 ? filteredOptions.length - 1 : prev - 1,
        )
        break
      }

      case 'Enter': {
        if (open && activeIndex >= 0 && activeIndex < filteredOptions.length) {
          event.preventDefault()
          onChange(filteredOptions[activeIndex])
          setOpen(false)
          setActiveIndex(-1)
        }
        break
      }

      case 'Escape': {
        if (!open) break
        event.preventDefault()
        event.stopPropagation()
        setOpen(false)
        setActiveIndex(-1)
        break
      }

      case 'Tab': {
        setOpen(false)
        setActiveIndex(-1)
        break
      }

      default:
        break
    }
  }

  const showList = open && !disabled

  useEffect(() => {
    if (activeIndex >= 0) {
      document.getElementById(`${listId}-option-${activeIndex}`)?.scrollIntoView({ block: 'nearest' })
    }
  }, [activeIndex, listId])
  const hasQuery = value.trim().length > 0

  // Уникальный ID активной опции для aria-activedescendant
  const activeId =
    activeIndex >= 0 && activeIndex < filteredOptions.length
      ? `${listId}-option-${activeIndex}`
      : undefined

  return (
    <div
      ref={rootRef}
      className={['filter-combobox', className].filter(Boolean).join(' ')}
    >
      <label className="echat-field">
        <span className="echat-field__label">{label}</span>
        <input
          className="echat-field__input"
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          role="combobox"
          aria-expanded={showList}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={activeId}
          onChange={(event) => {
            onChange(event.target.value)
            setOpen(true)
            setActiveIndex(-1)
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
        />
      </label>
      {showList ? (
        <ul id={listId} className="filter-combobox__list" role="listbox">
          {!hasQuery && !listAllOnFocus ? (
            <li className="filter-combobox__hint">Введите название для поиска</li>
          ) : null}
          {hasQuery && filteredOptions.length === 0 ? (
            <li className="filter-combobox__hint">Ничего не найдено</li>
          ) : null}
          {filteredOptions.map((option, index) => (
            <li
              key={option}
              id={`${listId}-option-${index}`}
              role="option"
              aria-selected={activeIndex === index || option === value}
            >
              <button
                type="button"
                className="filter-combobox__option"
                tabIndex={-1}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  onChange(option)
                  setOpen(false)
                  setActiveIndex(-1)
                }}
                onMouseEnter={() => setActiveIndex(index)}
              >
                {option}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
